import { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

import { useAuth } from "@/auth";
import { ApiError } from "@/lib/api";
import ChannelOrderList, {
  type OrderableChannel,
} from "@/components/ChannelOrderList";
import ChannelToggle from "@/components/ChannelToggle";
import PushDevicesList from "@/components/PushDevicesList";
import TelegramBinding from "@/components/TelegramBinding";
import {
  DEFAULT_NOTIFICATION_PREFERENCES,
  deleteNotificationPreferences,
  deleteTelegramBinding,
  getNotificationPreferences,
  putNotificationPreferences,
  type NotificationChannelName,
  type NotificationPreferencesPayload,
} from "@/lib/notifications-api";
import {
  isPushSupported,
  PushPermissionDeniedError,
  subscribeToPush,
  unsubscribeFromPush,
} from "@/lib/push-subscribe";

/**
 * apps/web :: components :: NotificationPreferences
 *
 * The interactive form rendered inside `/settings`. Owns load/save/delete
 * lifecycle for the caller's `NotificationPreferencesPayload` and enforces
 * the same policy the backend does:
 *   - Email OR InAppPush must stay enabled (at least one transactional path).
 *   - Opt-in channels (WhatsApp / Telegram / SMS) require their contact
 *     field populated before the toggle may remain on.
 *
 * InAppPush toggle wiring (ADR-0008):
 *   - Turning ON: persists the preference AND runs the browser-side subscribe
 *     flow (request permission → PushManager.subscribe → POST /push/subscribe).
 *   - Permission denied: revert the toggle in-place and render an inline error.
 *   - Turning OFF: call `unsubscribeFromPush` non-blocking AND persist via PUT.
 *   - Feature-detect: when the browser doesn't support Push, hide the toggle
 *     entirely AND force Email as the only transactional channel.
 *
 * The PushDevicesList component renders below the toggle when push is on.
 */
interface LoadState {
  kind: "loading" | "ready" | "error";
  errorMessage?: string;
}

interface SaveState {
  kind: "idle" | "saving" | "success" | "error";
  errorMessage?: string;
}

/** Phone-ish E.164-adjacent validator: `+` + 8-15 digits. Keeps UX permissive. */
const E164_REGEX = /^\+?[0-9]{8,15}$/;

/**
 * Map each channel in the wire-format `preferenceOrder` onto the boolean
 * enabled flag that drives its toggle. Used to (a) derive the ordered
 * list of enabled channels that `<ChannelOrderList />` renders, and (b)
 * reconcile `preferenceOrder` whenever a toggle flips on/off.
 *
 * Kept in sync with `NotificationChannelName` in `notifications-api.ts`.
 */
const CHANNEL_ENABLED_FIELD: Record<
  NotificationChannelName,
  keyof NotificationPreferencesPayload
> = {
  email: "emailEnabled",
  in_app_push: "inAppPushEnabled",
  whatsapp: "whatsappEnabled",
  telegram: "telegramEnabled",
  sms: "smsEnabled",
};

const ALL_CHANNEL_NAMES: NotificationChannelName[] = [
  "email",
  "in_app_push",
  "whatsapp",
  "telegram",
  "sms",
];

/**
 * Reconcile `preferenceOrder` against the current enabled flags on the
 * payload. Enabled channels not in the order get appended at the end
 * (preserving the historical priority for pre-existing ones); disabled
 * channels are stripped. Returns a new payload so callers can feed it
 * straight into `setPrefs`.
 */
function reconcilePreferenceOrder(
  payload: NotificationPreferencesPayload,
): NotificationPreferencesPayload {
  const enabledSet = new Set<NotificationChannelName>(
    ALL_CHANNEL_NAMES.filter(
      (name) => payload[CHANNEL_ENABLED_FIELD[name]] === true,
    ),
  );
  const kept = payload.preferenceOrder.filter((name) => enabledSet.has(name));
  const appended: NotificationChannelName[] = [];
  for (const name of enabledSet) {
    if (!kept.includes(name)) {
      appended.push(name);
    }
  }
  const next = [...kept, ...appended];
  // Reference-equality optimisation: only return a new object if the
  // reconciled order actually differs.
  if (
    next.length === payload.preferenceOrder.length &&
    next.every((n, i) => n === payload.preferenceOrder[i])
  ) {
    return payload;
  }
  return { ...payload, preferenceOrder: next };
}

export default function NotificationPreferences() {
  const { t } = useTranslation();
  const { state: authState } = useAuth();

  const [initial, setInitial] = useState<NotificationPreferencesPayload | null>(null);
  const [prefs, setPrefs] = useState<NotificationPreferencesPayload>(
    DEFAULT_NOTIFICATION_PREFERENCES,
  );
  const [load, setLoad] = useState<LoadState>({ kind: "loading" });
  const [save, setSave] = useState<SaveState>({ kind: "idle" });
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [deleteState, setDeleteState] = useState<SaveState>({ kind: "idle" });

  // Push-subscribe UX state.
  const [pushSupported] = useState<boolean>(() => isPushSupported());
  const [pushError, setPushError] = useState<string | null>(null);
  const [pushBusy, setPushBusy] = useState<boolean>(false);
  // Telegram binding UX state.
  const [telegramModalOpen, setTelegramModalOpen] = useState<boolean>(false);
  const [telegramError, setTelegramError] = useState<string | null>(null);
  // Bumped after each successful subscribe/unsubscribe/remove so the
  // PushDevicesList can re-fetch. Avoids hoisting the list state up here.
  const [devicesRefreshKey, setDevicesRefreshKey] = useState<number>(0);

  const refresh = useCallback(async () => {
    setLoad({ kind: "loading" });
    try {
      const data = await getNotificationPreferences();
      setPrefs(data);
      setInitial(data);
      setLoad({ kind: "ready" });
    } catch (err) {
      const msg =
        err instanceof ApiError
          ? err.message
          : err instanceof Error
            ? err.message
            : String(err);
      setLoad({ kind: "error", errorMessage: msg });
    }
  }, []);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  // ---------- derived ----------
  const isDirty = useMemo(() => {
    if (initial === null) return false;
    return JSON.stringify(initial) !== JSON.stringify(prefs);
  }, [initial, prefs]);

  const validation = useMemo(() => validate(prefs), [prefs]);

  const canSave =
    isDirty && !validation.blocking && load.kind === "ready" && save.kind !== "saving";

  // ---------- handlers ----------
  function patch(update: Partial<NotificationPreferencesPayload>) {
    setPrefs((p) => {
      const merged = { ...p, ...update };
      // Keep preferenceOrder in lockstep with enabled flags: toggling a
      // channel on appends it; toggling off removes it. Only run when the
      // caller didn't supply an explicit `preferenceOrder` override.
      if (update.preferenceOrder === undefined) {
        return reconcilePreferenceOrder(merged);
      }
      return merged;
    });
    setSave({ kind: "idle" });
  }

  /**
   * Build the `OrderableChannel[]` passed into `<ChannelOrderList />`. Only
   * enabled channels render. The ordering respects `prefs.preferenceOrder`
   * (that's the source of truth users can drag-reorder).
   */
  const orderedChannels = useMemo<OrderableChannel[]>(() => {
    const labels: Record<NotificationChannelName, string> = {
      email: t("notifications.channel.email.label"),
      in_app_push: t("notifications.channel.inAppPush.label"),
      whatsapp: t("notifications.channel.whatsapp.label"),
      telegram: t("notifications.channel.telegram.label"),
      sms: t("notifications.channel.sms.label"),
    };
    return prefs.preferenceOrder
      .filter((name) => prefs[CHANNEL_ENABLED_FIELD[name]] === true)
      .map((name) => ({ id: name, label: labels[name] }));
  }, [prefs, t]);

  function handleReorder(next: OrderableChannel[]) {
    const nextOrder = next.map((c) => c.id);
    setPrefs((p) => {
      // Preserve any currently-enabled channels that weren't in the reorder
      // list (defensive — shouldn't happen because we only render enabled
      // rows, but protects against races with a concurrent toggle).
      const missingEnabled = ALL_CHANNEL_NAMES.filter(
        (name) => p[CHANNEL_ENABLED_FIELD[name]] === true && !nextOrder.includes(name),
      );
      return {
        ...p,
        preferenceOrder: [...nextOrder, ...missingEnabled],
      };
    });
    setSave({ kind: "idle" });
  }

  /**
   * Flip the InAppPush toggle + run the browser subscribe/unsubscribe dance.
   * Keeps the preference-level flip and the browser-level flip in lockstep:
   *   - ON, permission granted  → preference flips on, server row persisted.
   *   - ON, permission denied   → preference stays off, inline error shown.
   *   - OFF                     → preference flips off, best-effort unsub.
   */
  async function handleInAppPushToggle(next: boolean) {
    setPushError(null);
    if (!pushSupported) {
      // Should not happen (toggle hides when unsupported), but defensive.
      return;
    }
    if (next) {
      setPushBusy(true);
      // Optimistic UI: flip first, revert on failure so the user doesn't
      // have to wait for the permission prompt to move the switch.
      patch({ inAppPushEnabled: true });
      try {
        await subscribeToPush();
        setDevicesRefreshKey((k) => k + 1);
      } catch (err) {
        patch({ inAppPushEnabled: false });
        if (err instanceof PushPermissionDeniedError) {
          setPushError(t("notifications.pushPermission.denied"));
        } else {
          const msg = err instanceof Error ? err.message : String(err);
          setPushError(t("notifications.pushPermission.error", { message: msg }));
        }
      } finally {
        setPushBusy(false);
      }
    } else {
      patch({ inAppPushEnabled: false });
      // Non-blocking: failure here is logged in the helper; the preference
      // PUT happens on Save regardless.
      setPushBusy(true);
      try {
        await unsubscribeFromPush();
        setDevicesRefreshKey((k) => k + 1);
      } finally {
        setPushBusy(false);
      }
    }
  }

  /**
   * Handle the Telegram toggle.
   *   - ON  → open the binding modal; actual preference flip happens in
   *           `handleTelegramVerified` after the backend reports verified.
   *   - OFF → call DELETE /api/notifications/telegram/binding (server
   *           removes chat_id + flips the preference row). Refresh.
   */
  async function handleTelegramToggle(next: boolean) {
    setTelegramError(null);
    if (next) {
      setTelegramModalOpen(true);
    } else {
      try {
        await deleteTelegramBinding();
      } catch (err) {
        const msg =
          err instanceof ApiError
            ? err.message
            : err instanceof Error
              ? err.message
              : String(err);
        setTelegramError(
          t("notifications.telegram.disconnectError", { message: msg }),
        );
      }
      patch({ telegramEnabled: false, telegramChatId: null });
    }
  }

  /** Binding modal reports success — flip the toggle on locally. */
  function handleTelegramVerified() {
    setTelegramModalOpen(false);
    patch({ telegramEnabled: true });
  }

  async function handleSave() {
    if (!canSave) return;
    setSave({ kind: "saving" });
    try {
      const echoed = await putNotificationPreferences(prefs);
      setPrefs(echoed);
      setInitial(echoed);
      setSave({ kind: "success" });
    } catch (err) {
      const msg =
        err instanceof ApiError
          ? err.message
          : err instanceof Error
            ? err.message
            : String(err);
      setSave({ kind: "error", errorMessage: msg });
    }
  }

  async function handleDelete() {
    setDeleteState({ kind: "saving" });
    try {
      await deleteNotificationPreferences();
      const fresh = { ...DEFAULT_NOTIFICATION_PREFERENCES };
      setPrefs(fresh);
      setInitial(fresh);
      setConfirmDelete(false);
      setDeleteState({ kind: "success" });
      setSave({ kind: "idle" });
    } catch (err) {
      const msg =
        err instanceof ApiError
          ? err.message
          : err instanceof Error
            ? err.message
            : String(err);
      setDeleteState({ kind: "error", errorMessage: msg });
    }
  }

  // ---------- render ----------
  if (load.kind === "loading") {
    return (
      <div
        role="status"
        aria-live="polite"
        className="rounded-md border border-brand-primary/10 bg-white p-md text-sm text-neutral-700"
        data-testid="prefs-loading"
      >
        {t("notifications.loading")}
      </div>
    );
  }
  if (load.kind === "error") {
    return (
      <div
        role="alert"
        className="rounded-md border border-danger/40 bg-danger/5 p-md text-sm text-danger"
        data-testid="prefs-load-error"
      >
        {t("notifications.loadError", { message: load.errorMessage ?? "" })}
      </div>
    );
  }

  const userEmail =
    authState.kind === "authenticated" ? authState.user.email : "";

  return (
    <form
      className="space-y-md"
      onSubmit={(ev) => {
        ev.preventDefault();
        void handleSave();
      }}
      data-testid="notification-preferences-form"
      noValidate
    >
      <ChannelToggle
        channelId="email"
        label={t("notifications.channel.email.label")}
        enabled={prefs.emailEnabled}
        onToggle={() => {
          /* forcedOn — toggle is disabled */
        }}
        forcedOn
        helperText={t("notifications.channel.email.helper", { email: userEmail })}
      />

      {pushSupported ? (
        <div data-testid="push-section">
          <ChannelToggle
            channelId="inAppPush"
            label={t("notifications.channel.inAppPush.label")}
            enabled={prefs.inAppPushEnabled}
            onToggle={(next) => {
              void handleInAppPushToggle(next);
            }}
            helperText={t("notifications.channel.inAppPush.helper")}
            errorText={pushError ?? undefined}
          />
          {prefs.inAppPushEnabled && !pushBusy && (
            <PushDevicesList
              refreshKey={devicesRefreshKey}
              onAfterRemove={() => setDevicesRefreshKey((k) => k + 1)}
            />
          )}
        </div>
      ) : (
        <div
          data-testid="push-unsupported-notice"
          className="rounded-md border border-brand-primary/10 bg-white p-md text-xs text-neutral-600"
        >
          {t("notifications.pushPermission.unsupported")}
        </div>
      )}

      <ChannelToggle
        channelId="whatsapp"
        label={t("notifications.channel.whatsapp.label")}
        enabled={prefs.whatsappEnabled}
        onToggle={(next) =>
          patch({
            whatsappEnabled: next,
            // Preserve existing number when toggling off so a re-enable doesn't wipe it.
          })
        }
        helperText={t("notifications.stepUpNote")}
        contactField={{
          label: t("notifications.channel.whatsapp.contactLabel"),
          value: prefs.whatsappNumber ?? "",
          onChange: (v) =>
            patch({ whatsappNumber: v.trim() === "" ? null : v }),
          placeholder: "+15551234567",
          inputMode: "tel",
          required: prefs.whatsappEnabled,
          helperText: t("notifications.channel.whatsapp.contactHelper"),
        }}
        errorText={validation.errors.whatsapp}
      />

      <ChannelToggle
        channelId="telegram"
        label={t("notifications.channel.telegram.label")}
        enabled={prefs.telegramEnabled}
        onToggle={(next) => {
          void handleTelegramToggle(next);
        }}
        helperText={
          prefs.telegramEnabled
            ? t("notifications.channel.telegram.contactHelper")
            : t("notifications.stepUpNote")
        }
        errorText={telegramError ?? validation.errors.telegram}
      />

      {telegramModalOpen && (
        <TelegramBinding
          onVerified={handleTelegramVerified}
          onCancel={() => setTelegramModalOpen(false)}
        />
      )}

      <ChannelToggle
        channelId="sms"
        label={t("notifications.channel.sms.label")}
        enabled={prefs.smsEnabled}
        onToggle={(next) => patch({ smsEnabled: next })}
        helperText={t("notifications.stepUpNote")}
        contactField={{
          label: t("notifications.channel.sms.contactLabel"),
          value: prefs.smsNumber ?? "",
          onChange: (v) => patch({ smsNumber: v.trim() === "" ? null : v }),
          placeholder: "+15551234567",
          inputMode: "tel",
          required: prefs.smsEnabled,
          helperText: t("notifications.channel.sms.contactHelper"),
        }}
        errorText={validation.errors.sms}
      />

      <section
        className="rounded-md border border-brand-primary/10 bg-white p-md"
        data-testid="channel-order-section"
      >
        <h3 className="text-sm font-semibold text-brand-primary">
          {t("notifications.channelOrder.sectionTitle")}
        </h3>
        <p className="mt-xs mb-sm text-xs text-neutral-600">
          {t("notifications.channelOrder.sectionHelper")}
        </p>
        <ChannelOrderList channels={orderedChannels} onChange={handleReorder} />
      </section>

      <hr className="border-brand-primary/10" />

      <section
        className="rounded-md border border-brand-primary/10 bg-white p-md"
        data-testid="marketing-consent-row"
      >
        <div className="flex items-start justify-between gap-md">
          <div className="flex-1">
            <label
              htmlFor="marketing-consent"
              className="text-sm font-semibold text-brand-primary"
            >
              {t("notifications.marketing.label")}
            </label>
            <p className="mt-xs text-xs text-neutral-600">
              {t("notifications.marketing.helper")}
            </p>
          </div>
          <div className="shrink-0">
            <label className="relative inline-flex cursor-pointer items-center">
              <input
                id="marketing-consent"
                type="checkbox"
                role="switch"
                checked={prefs.marketingConsent}
                onChange={(ev) => patch({ marketingConsent: ev.target.checked })}
                data-testid="marketing-consent-toggle"
                className="peer sr-only"
              />
              <span
                aria-hidden="true"
                className="h-6 w-11 rounded-full bg-neutral-300 transition-colors after:absolute after:top-0.5 after:left-0.5 after:h-5 after:w-5 after:rounded-full after:bg-white after:shadow after:transition-transform peer-checked:bg-brand-primary peer-checked:after:translate-x-5"
              />
            </label>
          </div>
        </div>
      </section>

      {validation.transportError && (
        <div
          role="alert"
          data-testid="transport-error"
          className="rounded-md border border-danger/40 bg-danger/5 p-md text-sm font-medium text-danger"
        >
          {validation.transportError}
        </div>
      )}

      {save.kind === "error" && (
        <div
          role="alert"
          data-testid="save-error"
          className="rounded-md border border-danger/40 bg-danger/5 p-md text-sm font-medium text-danger"
        >
          {t("notifications.saveError", { message: save.errorMessage ?? "" })}
        </div>
      )}
      {save.kind === "success" && (
        <div
          role="status"
          data-testid="save-success"
          className="rounded-md border border-success/40 bg-success/5 p-md text-sm font-medium text-success"
        >
          {t("notifications.saveSuccess")}
        </div>
      )}

      <div className="flex flex-wrap items-center justify-between gap-md pt-md">
        <button
          type="submit"
          disabled={!canSave}
          data-testid="save-button"
          className="inline-flex items-center justify-center rounded-md bg-brand-primary px-md py-sm text-sm font-semibold text-white transition-colors hover:bg-brand-primary/90 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand-primary disabled:cursor-not-allowed disabled:opacity-50"
        >
          {save.kind === "saving"
            ? t("notifications.saving")
            : t("notifications.save")}
        </button>
        {!confirmDelete ? (
          <button
            type="button"
            onClick={() => setConfirmDelete(true)}
            data-testid="delete-button"
            className="text-xs font-medium text-danger underline hover:no-underline"
          >
            {t("notifications.delete.cta")}
          </button>
        ) : (
          <div
            role="alertdialog"
            aria-labelledby="delete-confirm-title"
            aria-describedby="delete-confirm-body"
            data-testid="delete-confirm"
            className="flex flex-col gap-sm rounded-md border border-danger/40 bg-danger/5 p-md text-sm"
          >
            <p id="delete-confirm-title" className="font-semibold text-danger">
              {t("notifications.delete.confirmTitle")}
            </p>
            <p id="delete-confirm-body" className="text-neutral-800">
              {t("notifications.delete.confirmBody")}
            </p>
            <div className="flex gap-sm">
              <button
                type="button"
                onClick={() => void handleDelete()}
                disabled={deleteState.kind === "saving"}
                data-testid="delete-confirm-button"
                className="inline-flex items-center justify-center rounded-md bg-danger px-md py-xs text-xs font-semibold text-white hover:opacity-90 disabled:opacity-50"
              >
                {deleteState.kind === "saving"
                  ? t("notifications.delete.deleting")
                  : t("notifications.delete.confirm")}
              </button>
              <button
                type="button"
                onClick={() => setConfirmDelete(false)}
                data-testid="delete-cancel-button"
                className="inline-flex items-center justify-center rounded-md border border-brand-primary/20 bg-white px-md py-xs text-xs font-medium text-brand-primary"
              >
                {t("notifications.delete.cancel")}
              </button>
            </div>
            {deleteState.kind === "error" && (
              <p role="alert" className="text-xs font-medium text-danger">
                {deleteState.errorMessage}
              </p>
            )}
          </div>
        )}
      </div>
    </form>
  );
}

// ---------------------------------------------------------------------------
// Validation
// ---------------------------------------------------------------------------

interface ValidationResult {
  /** Any validation issue blocks save. */
  blocking: boolean;
  /** Aggregate transport-level message (shown above Save button). */
  transportError?: string;
  /** Per-channel messages shown inline on the channel row. */
  errors: Partial<Record<"whatsapp" | "telegram" | "sms", string>>;
}

function validate(p: NotificationPreferencesPayload): ValidationResult {
  const errors: ValidationResult["errors"] = {};
  let blocking = false;
  let transportError: string | undefined;

  if (!p.emailEnabled && !p.inAppPushEnabled) {
    blocking = true;
    // Fallback english string; paired i18n key gives a localised version at render.
    // We keep the en string here for components that can't see `t()` (tests).
    transportError =
      "At least one of Email or In-app Push must remain enabled for account security.";
  }

  if (p.whatsappEnabled) {
    const v = (p.whatsappNumber ?? "").trim();
    if (v === "") {
      errors.whatsapp = "Enter a phone number to enable WhatsApp.";
      blocking = true;
    } else if (!E164_REGEX.test(v)) {
      errors.whatsapp = "Use international format, e.g. +15551234567.";
      blocking = true;
    }
  }
  // Telegram no longer needs manual chat-id entry — the bot-binding flow
  // captures it automatically once the user pairs via the deep link.
  if (p.smsEnabled) {
    const v = (p.smsNumber ?? "").trim();
    if (v === "") {
      errors.sms = "Enter a phone number to enable SMS.";
      blocking = true;
    } else if (!E164_REGEX.test(v)) {
      errors.sms = "Use international format, e.g. +15551234567.";
      blocking = true;
    }
  }

  return { blocking, transportError, errors };
}
